import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { COLORS, TIER_NAMES, TIER_COLORS } from '../data/mockData';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import VideoWithSoundToggle from '../components/VideoWithSoundToggle';
import { fetchResearch } from '../services/api';
import { FlaskConical, Clock, BookOpen, ArrowRight, ArrowLeft, Lock, X } from 'lucide-react';

const CATEGORY_LABELS = {
  sleep: 'Sleep', nutrition: 'Nutrition', exercise: 'Exercise',
  social: 'Social', mental_wellness: 'Mental Wellness', supplements: 'Supplements', general: 'General',
};
const CATEGORY_COLORS = {
  sleep: '#5C6BC0', nutrition: '#66BB6A', exercise: '#EF5350',
  social: '#42A5F5', mental_wellness: '#FF9800', supplements: '#8D6E63', general: '#6B8E23',
};

export default function ResearchPage() {
  const [articles, setArticles] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);

  useEffect(() => {
    fetchResearch().then(data => { setArticles(data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const categories = ['all', ...new Set(articles.map(a => a.category))];
  const filtered = filter === 'all' ? articles : articles.filter(a => a.category === filter);

  // Article detail view
  if (selectedArticle) {
    const catColor = CATEGORY_COLORS[selectedArticle.category] || COLORS.primary;
    return (
      <div style={{ backgroundColor: COLORS.background, minHeight: '100vh' }}>
        <SiteHeader />
        <div style={{ maxWidth: 760, margin: '0 auto', padding: '32px 20px 56px' }}>
          <button data-testid="back-to-research" onClick={() => setSelectedArticle(null)} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', marginBottom: 20, color: COLORS.textSecondary, fontSize: 14, fontWeight: 600 }}>
            <ArrowLeft size={18} /> Back to Research Library
          </button>

          <article data-testid="article-detail" style={{ backgroundColor: COLORS.surface, borderRadius: 20, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
            {/* Header */}
            <div style={{ backgroundColor: catColor, padding: '28px 32px' }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <span style={{ padding: '4px 12px', borderRadius: 9999, fontSize: 12, fontWeight: 700, backgroundColor: 'rgba(255,255,255,0.2)', color: '#FFF' }}>
                  {CATEGORY_LABELS[selectedArticle.category] || selectedArticle.category}
                </span>
                {selectedArticle.tier_required > 0 && (
                  <span style={{ padding: '4px 12px', borderRadius: 9999, fontSize: 12, fontWeight: 700, backgroundColor: 'rgba(255,255,255,0.2)', color: '#FFF', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Lock size={12} /> {TIER_NAMES[selectedArticle.tier_required]}+
                  </span>
                )}
              </div>
              <h1 style={{ fontSize: 28, fontWeight: 800, color: '#FFF', fontFamily: 'Outfit, sans-serif', lineHeight: 1.2, margin: 0 }}>{selectedArticle.title}</h1>
              <div style={{ display: 'flex', gap: 16, marginTop: 12 }}>
                {selectedArticle.source && <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', gap: 4 }}><BookOpen size={14} /> {selectedArticle.source}</span>}
                <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', gap: 4 }}><Clock size={14} /> {selectedArticle.read_time}</span>
                {selectedArticle.published && <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>{selectedArticle.published}</span>}
              </div>
            </div>

            {/* Content */}
            <div style={{ padding: '28px 32px' }}>
              {selectedArticle.summary && (
                <div style={{ backgroundColor: `${catColor}08`, borderLeft: `4px solid ${catColor}`, borderRadius: '0 10px 10px 0', padding: '16px 20px', marginBottom: 24 }}>
                  <p style={{ fontSize: 16, color: COLORS.textPrimary, lineHeight: 1.6, margin: 0, fontWeight: 500 }}>{selectedArticle.summary}</p>
                </div>
              )}

              {selectedArticle.content ? (
                <div style={{ fontSize: 15, color: COLORS.textPrimary, lineHeight: 1.8, whiteSpace: 'pre-line' }}>
                  {selectedArticle.content}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '32px 0', color: COLORS.textSecondary }}>
                  <BookOpen size={40} color={COLORS.border} style={{ marginBottom: 12 }} />
                  <p style={{ fontSize: 15 }}>Full article content is not yet available.</p>
                  <p style={{ fontSize: 13 }}>Check back soon or explore other articles in our library.</p>
                </div>
              )}
            </div>
          </article>
        </div>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: COLORS.background, minHeight: '100vh' }}>
      <SiteHeader />

      {/* Hero */}
      <section style={{ background: 'linear-gradient(180deg, #F9F6F0 0%, #E8ECD7 50%, #F9F6F0 100%)', padding: '64px 20px 48px', textAlign: 'center' }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.primary, letterSpacing: 2, textTransform: 'uppercase' }}>RESEARCH LIBRARY</span>
          <h1 data-testid="research-title" style={{ fontSize: 'clamp(32px, 5vw, 48px)', fontWeight: 900, color: COLORS.textPrimary, lineHeight: 1.15, letterSpacing: -1, marginTop: 12, fontFamily: 'Outfit, sans-serif' }}>
            Science of Longevity
          </h1>
          <p style={{ fontSize: 'clamp(16px, 2vw, 18px)', color: COLORS.textSecondary, lineHeight: 1.6, marginTop: 16 }}>
            Explore peer-reviewed research, meta-analyses, and evidence-based insights from the world's leading longevity institutions.
          </p>
        </div>
      </section>

      {/* INTRO VIDEO (under banner, top-middle, 65% size) */}
      <section data-testid="research-intro-video-section" style={{ padding: '20px 20px 0' }}>
        <VideoWithSoundToggle
          src="/media/research-intro.mp4"
          testId="research-intro-video"
          aspectRatio="9 / 16"
          maxWidth={126}
          objectFit="contain"
        />
      </section>

      {/* Filter */}
      <section style={{ padding: '0 20px 32px' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
            {categories.map(c => (
              <button key={c} data-testid={`research-filter-${c}`} onClick={() => setFilter(c)}
                style={{ padding: '8px 18px', borderRadius: 9999, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, textTransform: 'capitalize', backgroundColor: filter === c ? COLORS.primary : COLORS.surface, color: filter === c ? '#FFF' : COLORS.textSecondary, transition: 'all 0.2s' }}>
                {c === 'all' ? 'All Topics' : (CATEGORY_LABELS[c] || c)}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section style={{ padding: '0 20px 56px' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: COLORS.textSecondary }}>Loading research...</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: COLORS.textSecondary }}>No articles found for this filter.</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20 }}>
              {filtered.filter(a => a.title).map(article => {
                const catColor = CATEGORY_COLORS[article.category] || COLORS.primary;
                return (
                  <button key={article.article_id} data-testid={`research-article-${article.article_id}`}
                    onClick={() => setSelectedArticle(article)}
                    style={{ backgroundColor: COLORS.surface, borderRadius: 20, padding: 0, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', transition: 'transform 0.3s, box-shadow 0.3s', display: 'flex', flexDirection: 'column', border: 'none', cursor: 'pointer', textAlign: 'left', overflow: 'hidden' }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)'; }}>
                    {/* Color strip */}
                    <div style={{ height: 4, backgroundColor: catColor }} />
                    <div style={{ padding: 24, flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                        <span style={{ padding: '4px 10px', borderRadius: 8, fontSize: 12, fontWeight: 700, backgroundColor: `${catColor}18`, color: catColor, textTransform: 'capitalize' }}>
                          {CATEGORY_LABELS[article.category] || article.category}
                        </span>
                        {article.tier_required > 0 && (
                          <span style={{ padding: '4px 10px', borderRadius: 8, fontSize: 12, fontWeight: 700, backgroundColor: `${TIER_COLORS[article.tier_required]}18`, color: TIER_COLORS[article.tier_required], display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Lock size={12} /> {TIER_NAMES[article.tier_required]}+
                          </span>
                        )}
                      </div>
                      <h3 style={{ fontSize: 18, fontWeight: 700, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif', marginBottom: 8, lineHeight: 1.3 }}>{article.title}</h3>
                      <p style={{ fontSize: 14, color: COLORS.textSecondary, lineHeight: 1.5, flex: 1 }}>{article.summary}</p>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, paddingTop: 14, borderTop: `1px solid ${COLORS.border}40` }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          {article.source && <span style={{ fontSize: 12, color: COLORS.textSecondary, display: 'flex', alignItems: 'center', gap: 4 }}><BookOpen size={14} /> {article.source}</span>}
                          <span style={{ fontSize: 12, color: COLORS.textSecondary, display: 'flex', alignItems: 'center', gap: 4 }}><Clock size={14} /> {article.read_time}</span>
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.primary, display: 'flex', alignItems: 'center', gap: 4 }}>Read <ArrowRight size={14} /></span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: 'linear-gradient(135deg, #6B8E23, #4a6e18)', padding: '48px 20px', textAlign: 'center' }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <h2 style={{ fontSize: 24, fontWeight: 800, color: '#FFF', fontFamily: 'Outfit, sans-serif' }}>Unlock All Research</h2>
          <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.8)', marginTop: 8, marginBottom: 20 }}>Get full access to all articles and meta-analyses with a free Explorer account.</p>
          <Link to="/register" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, backgroundColor: '#FFF', borderRadius: 9999, padding: '14px 28px', textDecoration: 'none' }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: COLORS.primary }}>Start Free</span>
            <ArrowRight size={18} color={COLORS.primary} />
          </Link>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
